import { Box, Skeleton, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import html2canvas from "html2canvas";
import React, { useRef, useState } from "react";

export const EmailViewer = ({ content, selectedImage, setSelectedImage }) => {
	const emailRef = useRef(null);
	const selectionRef = useRef(null);
	const sanitizedHTML = DOMPurify.sanitize(content);

	const [startPos, setStartPos] = useState(null);
	const [isSelecting, setIsSelecting] = useState(false);
	const [isLoadingPreview, setIsLoadingPreview] = useState(false);

	// Handle mouse down to start selection
	const handleMouseDown = (e) => {
		setIsSelecting(true);
		const rect = emailRef.current.getBoundingClientRect();
		setStartPos({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		});
	};

	// Handle mouse move to resize selection
	const handleMouseMove = (e) => {
		if (!isSelecting || !selectionRef.current || !startPos) return;
		const rect = emailRef.current.getBoundingClientRect();
		const currentX = e.clientX - rect.left;
		const currentY = e.clientY - rect.top;

		const left = Math.min(startPos.x, currentX);
		const top = Math.min(startPos.y, currentY);
		const width = Math.abs(currentX - startPos.x);
		const height = Math.abs(currentY - startPos.y);

		Object.assign(selectionRef.current.style, {
			left: `${left}px`,
			top: `${top}px`,
			width: `${width}px`,
			height: `${height}px`,
			display: "block",
		});
	};

	// Handle mouse up to capture selection
	const handleMouseUp = async () => {
		setIsSelecting(false);
		setIsLoadingPreview(true);
		if (!selectionRef.current || !emailRef.current) return;

		const { left, top, width, height } =
			selectionRef.current.getBoundingClientRect();
		const containerRect = emailRef.current.getBoundingClientRect();

		const crop = {
			x: left - containerRect.left,
			y: top - containerRect.top,
			width,
			height,
		};

		// ⬇️ Screenshot after all images are ready
		const canvas = await html2canvas(emailRef.current, {
			scrollY: -window.scrollY,
			useCORS: true,
			allowTaint: true,
			logging: false,
		});

		const croppedCanvas = document.createElement("canvas");
		croppedCanvas.width = crop.width;
		croppedCanvas.height = crop.height;

		const ctx = croppedCanvas.getContext("2d");
		ctx.drawImage(
			canvas,
			crop.x,
			crop.y,
			crop.width,
			crop.height,
			0,
			0,
			crop.width,
			crop.height
		);

		const imgData = croppedCanvas.toDataURL("image/png");
		setSelectedImage(imgData);
		setIsLoadingPreview(false);
	};

	return (
		<Box sx={{ p: "30px" }}>
			<Typography
				variant="h4"
				component="h2"
				sx={{ textAlign: "center", mb: "20px" }}
			>
				Zaznacz treść do analizy AI
			</Typography>
			{selectedImage && (
				<Box mt={2}>
					<Typography>Podgląd zaznaczenia:</Typography>
					<Box
						sx={{
							border: "1px solid #ddd",
							mb: "10px",
							display: "inline-block",
						}}
					>
						{!isLoadingPreview && (
							<img
								src={selectedImage}
								alt="Zrzut ekranu"
								style={{ maxWidth: "100%", borderRadius: 8 }}
							/>
						)}
						{isLoadingPreview && (
							<Skeleton
								variant="rectangular"
								height={selectionRef.current.getBoundingClientRect().height}
								width={selectionRef.current.getBoundingClientRect().width}
							/>
						)}
					</Box>
				</Box>
			)}

			<Box sx={{ position: "relative" }}>
				<div
					ref={selectionRef}
					style={{
						position: "absolute",
						border: "2px dashed #3f51b5",
						backgroundColor: "rgba(63, 81, 181, 0.1)",
						display: "none",
						pointerEvents: "none",
						zIndex: 10,
					}}
				/>
				<Box
					ref={emailRef}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					sx={{
						border: "1px solid #ddd",
						padding: 2,
						userSelect: isSelecting ? "none" : "auto",
					}}
				>
					<div
						className="email-viewer"
						dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
					/>
				</Box>
			</Box>
		</Box>
	);
};
